import { useEffect } from 'react';
import { track } from '../../../../shared-web/track';
import { arrivalHref, type ChatArrival } from './chats';
import { navigate } from './router';

/**
 * The browser's own notification for a line that landed while the tab was away (2026-10-26 Riley, as WhatsApp Web):
 * the page's banner cannot be seen then, so the system says it, with Ember's own chime. Shown through the service
 * worker (public/sw.js), once for every open Ember tab and the one way every browser takes, the installed iPhone app
 * included; a click there
 * brings this tab forward and opens the conversation (`ember:open`). Only after the person said yes on the Chats
 * list's bar; nothing here ever asks on its own.
 */

export type NotifyState = 'unsupported' | NotificationPermission;

export function notifyState(): NotifyState {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  return Notification.permission;
}

/** The browser's question, asked from the person's tap on the bar. */
export async function askNotify(): Promise<NotifyState> {
  if (notifyState() === 'unsupported') return 'unsupported';
  try { return await Notification.requestPermission(); } catch { return notifyState(); }
}

/** What the notification says: the conversation's name over who said what, as the banner does. */
export function notificationOf(a: ChatArrival): { title: string; body: string } {
  return { title: a.state === 'ember' ? 'Ember' : a.name, body: a.who ? `${a.who}: ${a.text}` : a.text };
}

/**
 * Hand the arrival to the service worker, which says it once for every Ember tab and only while none is in front
 * (public/sw.js); false when it cannot (no yes, no worker). The tab it answers plays the chime.
 */
export async function notifyArrival(a: ChatArrival): Promise<boolean> {
  if (notifyState() !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (!reg.active) return false;
    const { title, body } = notificationOf(a);
    reg.active.postMessage({
      type: 'ember:notify', id: `${a.key}@${a.at}`, title, body, tag: a.key,
      data: { route: arrivalHref(a), state: a.state, match_id: a.roomId },
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Ember's chime (2026-10-26 Riley): two soft notes, drawn here rather than a file, the same on every machine. The
 * system's own sound stays off (the notification is silent). A tab the person never touched may be refused sound by
 * the browser; then it is quiet, and the notification still shows.
 */
let audio: AudioContext | null = null;
export function playChime(): void {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audio = audio ?? new Ctx();
    void audio.resume();
    const t0 = audio.currentTime + 0.02;
    [[880, 0], [1318.5, 0.14]].forEach(([hz, at]) => {
      const osc = audio!.createOscillator();
      const gain = audio!.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      gain.gain.setValueAtTime(0.0001, t0 + at);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + at + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.32);
      osc.connect(gain).connect(audio!.destination);
      osc.start(t0 + at);
      osc.stop(t0 + at + 0.34);
    });
  } catch { /* no sound: the notification still shows */ }
}

/** A notification was opened: the worker brought this tab forward; open its conversation. */
export const NOTIFY_OPEN_EVENT = 'ember:notify-open';

export function useNotifyOpen(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    const on = (e: MessageEvent): void => {
      const d = e.data as { type?: string; route?: string; state?: string; match_id?: string | null } | null;
      // The worker showed this tab's line: its chime, and the one record of it
      if (d?.type === 'ember:notified') {
        playChime();
        track('ui.seen', { name: 'chat_notify', state: d.state || '', ...(d.match_id ? { match_id: d.match_id } : {}) });
        return;
      }
      if (d?.type !== 'ember:open' || !d.route || !d.route.startsWith('/')) return;
      track('ui.clicked', { name: 'chat_notify', state: d.state || '', ...(d.match_id ? { match_id: d.match_id } : {}) });
      window.dispatchEvent(new Event(NOTIFY_OPEN_EVENT));
      navigate(d.route);
    };
    navigator.serviceWorker.addEventListener('message', on);
    return () => navigator.serviceWorker.removeEventListener('message', on);
  }, [enabled]);
}
